import { z } from "zod";

import { canonicalJson, sha256Hex, type JsonValue } from "./canonical.js";
import {
  signedDm2016CanonicalQuoteSchema,
  validateDm2016CanonicalQuoteIntegrity,
  verifySignedDm2016CanonicalQuote,
  type SignedDm2016CanonicalQuote,
} from "./dm2016-canonical-quote.js";

export const DM2016_DURABLE_ATTESTATION_SCHEMA_VERSION = "1.0.0" as const;
export const DM2016_ATTESTATION_KEYRING_SCHEMA_VERSION = "1.0.0" as const;
export const DM2016_DURABLE_ATTESTATION_ALGORITHM = "Ed25519" as const;

const identifierSchema = z.string().min(1).max(100).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const base64urlSchema = z.string().regex(/^[A-Za-z0-9_-]+$/);

export const dm2016AttestationPublicKeyringSchema = z.object({
  schemaVersion: z.literal(DM2016_ATTESTATION_KEYRING_SCHEMA_VERSION),
  keyringId: identifierSchema,
  keys: z.array(z.object({
    keyId: identifierSchema,
    algorithm: z.literal(DM2016_DURABLE_ATTESTATION_ALGORITHM),
    publicKeySpkiBase64url: base64urlSchema.min(40).max(200),
    validFrom: z.iso.datetime(),
    retiredAt: z.iso.datetime().optional(),
    status: z.enum(["active", "retired"]),
  }).strict()).min(1).max(20),
}).strict().superRefine((keyring, context) => {
  const identifiers = new Set<string>();
  let activeKeys = 0;
  keyring.keys.forEach((key, index) => {
    if (identifiers.has(key.keyId)) {
      context.addIssue({ code: "custom", path: ["keys", index, "keyId"], message: "Duplicate attestation key ID" });
    }
    identifiers.add(key.keyId);
    if (key.status === "active") {
      activeKeys += 1;
      if (key.retiredAt !== undefined) {
        context.addIssue({ code: "custom", path: ["keys", index, "retiredAt"], message: "Active key cannot have retiredAt" });
      }
    } else if (key.retiredAt === undefined || Date.parse(key.retiredAt) < Date.parse(key.validFrom)) {
      context.addIssue({ code: "custom", path: ["keys", index, "retiredAt"], message: "Retired key requires a valid retiredAt" });
    }
  });
  if (activeKeys !== 1) {
    context.addIssue({ code: "custom", path: ["keys"], message: "Keyring must contain exactly one active key" });
  }
});

export type Dm2016AttestationPublicKeyring = z.infer<typeof dm2016AttestationPublicKeyringSchema>;

export const dm2016DurableAttestationPayloadSchema = z.object({
  schemaVersion: z.literal(DM2016_DURABLE_ATTESTATION_SCHEMA_VERSION),
  canonicalDocumentSha256: sha256Schema,
  quoteSha256: sha256Schema,
  resultSha256: sha256Schema,
  calculationResultSha256: sha256Schema,
  originatingHmacKeyId: identifierSchema,
  quoteIssuedAt: z.iso.datetime(),
  quoteExpiresAt: z.iso.datetime(),
  serverVerifiedAt: z.iso.datetime(),
  releaseId: identifierSchema,
  releaseVersion: z.string().min(1).max(100),
  rulesetVersion: z.string().min(1).max(100),
  engineVersion: z.string().min(1).max(100),
  quoteSchemaVersion: z.string().min(1).max(100),
  interpretationMode: z.enum(["printed_source_strict", "professionearchitetto_v0_74"]),
  professionalUseAllowed: z.literal(true),
  legalCertification: z.literal(false),
  attestationKeyId: identifierSchema,
  attestationAlgorithm: z.literal(DM2016_DURABLE_ATTESTATION_ALGORITHM),
}).strict();

export type Dm2016DurableAttestationPayload = z.infer<
  typeof dm2016DurableAttestationPayloadSchema
>;

export const signedDurableAttestationReceiptSchema = z.object({
  payload: dm2016DurableAttestationPayloadSchema,
  signature: z.object({
    algorithm: z.literal(DM2016_DURABLE_ATTESTATION_ALGORITHM),
    keyId: identifierSchema,
    value: base64urlSchema.min(80).max(100),
  }).strict(),
}).strict();

export type SignedDurableAttestationReceipt = z.infer<
  typeof signedDurableAttestationReceiptSchema
>;

export interface Dm2016HistoricalVerificationResult {
  readonly integrityValid: boolean;
  readonly attestationValid: boolean;
  readonly currentVerificationWindow: boolean;
  readonly historicallyAuthentic: boolean;
  readonly checkedAt: string;
  readonly quoteSha256: string | null;
  readonly resultSha256: string | null;
  readonly attestationKeyId: string | null;
  readonly reasons: readonly string[];
}

export class Dm2016DurableAttestationError extends Error {
  constructor(
    readonly code:
      | "INVALID_ATTESTATION_INPUT"
      | "LIVE_QUOTE_VERIFICATION_FAILED"
      | "OPERATIONAL_RELEASE_GATE_CLOSED"
      | "ATTESTATION_SIGNING_NOT_CONFIGURED",
    message: string,
    readonly httpStatus: 400 | 409 | 503,
  ) {
    super(message);
    this.name = "Dm2016DurableAttestationError";
  }
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

function decodeBase64url(value: string): Uint8Array | null {
  if (!base64urlSchema.safeParse(value).success) return null;
  try {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/")
      .padEnd(Math.ceil(value.length / 4) * 4, "=");
    const binary = globalThis.atob(base64);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    return null;
  }
}

function encodeBase64url(value: Uint8Array): string {
  let binary = "";
  value.forEach((byte) => { binary += String.fromCharCode(byte); });
  return globalThis.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function toArrayBuffer(value: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(value.byteLength);
  copy.set(value);
  return copy.buffer;
}

async function importPrivateKey(value: string): Promise<CryptoKey> {
  const bytes = decodeBase64url(value);
  if (bytes === null) throw new Error("Malformed PKCS8 private key");
  return globalThis.crypto.subtle.importKey(
    "pkcs8",
    toArrayBuffer(bytes),
    { name: DM2016_DURABLE_ATTESTATION_ALGORITHM },
    false,
    ["sign"],
  );
}

async function importPublicKey(value: string): Promise<CryptoKey> {
  const bytes = decodeBase64url(value);
  if (bytes === null) throw new Error("Malformed SPKI public key");
  return globalThis.crypto.subtle.importKey(
    "spki",
    toArrayBuffer(bytes),
    { name: DM2016_DURABLE_ATTESTATION_ALGORITHM },
    false,
    ["verify"],
  );
}

function exactReceiptBinding(
  document: SignedDm2016CanonicalQuote,
  payload: Dm2016DurableAttestationPayload,
  canonicalDocumentSha256: string,
): boolean {
  const quote = document.payload;
  const serverVerifiedAt = Date.parse(payload.serverVerifiedAt);
  return payload.canonicalDocumentSha256 === canonicalDocumentSha256
    && payload.quoteSha256 === quote.hashes.quoteSha256
    && payload.resultSha256 === quote.hashes.resultSha256
    && payload.calculationResultSha256 === quote.hashes.calculationResultSha256
    && payload.originatingHmacKeyId === document.signature.keyId
    && payload.quoteIssuedAt === quote.issuedAt
    && payload.quoteExpiresAt === quote.expiresAt
    && !Number.isNaN(serverVerifiedAt)
    && serverVerifiedAt >= Date.parse(quote.issuedAt)
    && serverVerifiedAt <= Date.parse(quote.expiresAt)
    && payload.releaseId === quote.versions.releaseId
    && payload.releaseVersion === quote.versions.releaseVersion
    && payload.rulesetVersion === quote.versions.rulesetVersion
    && payload.engineVersion === quote.versions.engineVersion
    && payload.quoteSchemaVersion === quote.versions.quoteSchemaVersion
    && payload.interpretationMode === quote.versions.interpretationMode
    && payload.professionalUseAllowed === true
    && payload.legalCertification === false;
}

export async function createSignedDurableAttestationReceipt(input: {
  readonly document: SignedDm2016CanonicalQuote;
  readonly hmacSigningSecret: string;
  readonly expectedHmacKeyId: string;
  readonly attestationPrivateKeyPkcs8Base64url: string;
  readonly attestationKeyId: string;
  readonly publicKeyring: unknown;
  readonly operationalReleaseReady: boolean;
  readonly verifiedAt?: Date;
}): Promise<SignedDurableAttestationReceipt> {
  const documentParsed = signedDm2016CanonicalQuoteSchema.safeParse(input.document);
  const keyringParsed = dm2016AttestationPublicKeyringSchema.safeParse(input.publicKeyring);
  const verifiedAt = input.verifiedAt ?? new Date();
  if (!documentParsed.success || !keyringParsed.success || Number.isNaN(verifiedAt.getTime())) {
    throw new Dm2016DurableAttestationError("INVALID_ATTESTATION_INPUT", "Input attestazione durevole non valido.", 400);
  }
  if (!input.operationalReleaseReady) {
    throw new Dm2016DurableAttestationError("OPERATIONAL_RELEASE_GATE_CLOSED", "Gate operativo D.M. 2016 chiuso.", 503);
  }
  const document = documentParsed.data as unknown as SignedDm2016CanonicalQuote;
  const liveValid = await verifySignedDm2016CanonicalQuote({
    document,
    signingSecret: input.hmacSigningSecret,
    expectedKeyId: input.expectedHmacKeyId,
    at: verifiedAt,
  });
  if (!liveValid) {
    throw new Dm2016DurableAttestationError("LIVE_QUOTE_VERIFICATION_FAILED", "La quote HMAC corrente non è verificabile.", 409);
  }
  const activeKey = keyringParsed.data.keys.find((key) => key.status === "active");
  if (activeKey === undefined || activeKey.keyId !== input.attestationKeyId) {
    throw new Dm2016DurableAttestationError("ATTESTATION_SIGNING_NOT_CONFIGURED", "Key ID Ed25519 non coincide con la chiave pubblica attiva.", 503);
  }
  if (verifiedAt.getTime() < Date.parse(activeKey.validFrom)) {
    throw new Dm2016DurableAttestationError("ATTESTATION_SIGNING_NOT_CONFIGURED", "Chiave Ed25519 non ancora valida.", 503);
  }
  try {
    const canonicalDocumentSha256 = await sha256Hex(canonicalJson(document as unknown as JsonValue));
    const payload: Dm2016DurableAttestationPayload = {
      schemaVersion: DM2016_DURABLE_ATTESTATION_SCHEMA_VERSION,
      canonicalDocumentSha256,
      quoteSha256: document.payload.hashes.quoteSha256,
      resultSha256: document.payload.hashes.resultSha256,
      calculationResultSha256: document.payload.hashes.calculationResultSha256,
      originatingHmacKeyId: document.signature.keyId,
      quoteIssuedAt: document.payload.issuedAt,
      quoteExpiresAt: document.payload.expiresAt,
      serverVerifiedAt: verifiedAt.toISOString(),
      releaseId: document.payload.versions.releaseId,
      releaseVersion: document.payload.versions.releaseVersion,
      rulesetVersion: document.payload.versions.rulesetVersion,
      engineVersion: document.payload.versions.engineVersion,
      quoteSchemaVersion: document.payload.versions.quoteSchemaVersion,
      interpretationMode: document.payload.versions.interpretationMode,
      professionalUseAllowed: true,
      legalCertification: false,
      attestationKeyId: activeKey.keyId,
      attestationAlgorithm: DM2016_DURABLE_ATTESTATION_ALGORITHM,
    };
    const privateKey = await importPrivateKey(input.attestationPrivateKeyPkcs8Base64url);
    const encodedPayload = new TextEncoder().encode(canonicalJson(payload as unknown as JsonValue));
    const signatureBytes = new Uint8Array(await globalThis.crypto.subtle.sign(
      DM2016_DURABLE_ATTESTATION_ALGORITHM,
      privateKey,
      encodedPayload,
    ));
    const publicKey = await importPublicKey(activeKey.publicKeySpkiBase64url);
    const keyMatchesRegistry = await globalThis.crypto.subtle.verify(
      DM2016_DURABLE_ATTESTATION_ALGORITHM,
      publicKey,
      toArrayBuffer(signatureBytes),
      encodedPayload,
    );
    if (!keyMatchesRegistry) throw new Error("Private key does not match registry");
    return freezeDeep({
      payload,
      signature: {
        algorithm: DM2016_DURABLE_ATTESTATION_ALGORITHM,
        keyId: activeKey.keyId,
        value: encodeBase64url(signatureBytes),
      },
    });
  } catch (cause) {
    if (cause instanceof Dm2016DurableAttestationError) throw cause;
    throw new Dm2016DurableAttestationError(
      "ATTESTATION_SIGNING_NOT_CONFIGURED",
      "Configurazione firma Ed25519 assente, malformata o non coerente con il keyring.",
      503,
    );
  }
}

export async function verifyDm2016HistoricalAttestation(input: {
  readonly document: unknown;
  readonly receipt: unknown;
  readonly publicKeyring: unknown;
  readonly at?: Date;
}): Promise<Dm2016HistoricalVerificationResult> {
  const checkedAt = input.at ?? new Date();
  const documentParsed = signedDm2016CanonicalQuoteSchema.safeParse(input.document);
  const receiptParsed = signedDurableAttestationReceiptSchema.safeParse(input.receipt);
  const keyringParsed = dm2016AttestationPublicKeyringSchema.safeParse(input.publicKeyring);
  const reasons: string[] = [];
  if (Number.isNaN(checkedAt.getTime())) reasons.push("INVALID_CHECK_TIME");
  if (!documentParsed.success) reasons.push("INVALID_CANONICAL_QUOTE_STRUCTURE");
  if (!receiptParsed.success) reasons.push("INVALID_DURABLE_RECEIPT_STRUCTURE");
  if (!keyringParsed.success) reasons.push("INVALID_PUBLIC_KEYRING");
  if (
    !documentParsed.success
    || !receiptParsed.success
    || !keyringParsed.success
    || Number.isNaN(checkedAt.getTime())
  ) {
    return freezeDeep({
      integrityValid: false,
      attestationValid: false,
      currentVerificationWindow: false,
      historicallyAuthentic: false,
      checkedAt: Number.isNaN(checkedAt.getTime()) ? "invalid" : checkedAt.toISOString(),
      quoteSha256: null,
      resultSha256: null,
      attestationKeyId: null,
      reasons,
    });
  }
  const document = documentParsed.data as unknown as SignedDm2016CanonicalQuote;
  const receipt = receiptParsed.data;
  const keyring = keyringParsed.data;
  const integrityValid = await validateDm2016CanonicalQuoteIntegrity(document);
  if (!integrityValid) reasons.push("CANONICAL_QUOTE_INTEGRITY_FAILED");
  const issuedAt = Date.parse(document.payload.issuedAt);
  const expiresAt = Date.parse(document.payload.expiresAt);
  const currentVerificationWindow = !Number.isNaN(issuedAt)
    && !Number.isNaN(expiresAt)
    && checkedAt.getTime() >= issuedAt
    && checkedAt.getTime() <= expiresAt;
  const canonicalDocumentSha256 = await sha256Hex(canonicalJson(document as unknown as JsonValue));
  let attestationValid = false;
  const key = keyring.keys.find((candidate) => candidate.keyId === receipt.payload.attestationKeyId);
  if (key === undefined) {
    reasons.push("UNKNOWN_ATTESTATION_KEY");
  } else if (
    receipt.signature.keyId !== receipt.payload.attestationKeyId
    || receipt.signature.algorithm !== receipt.payload.attestationAlgorithm
    || !exactReceiptBinding(document, receipt.payload, canonicalDocumentSha256)
  ) {
    reasons.push("ATTESTATION_BINDING_FAILED");
  } else {
    const verifiedAt = Date.parse(receipt.payload.serverVerifiedAt);
    const keyValidAtIssuance = !Number.isNaN(verifiedAt)
      && verifiedAt >= Date.parse(key.validFrom)
      && (key.retiredAt === undefined || verifiedAt <= Date.parse(key.retiredAt));
    if (!keyValidAtIssuance) {
      reasons.push("ATTESTATION_KEY_NOT_VALID_AT_ISSUANCE");
    } else {
      try {
        const signature = decodeBase64url(receipt.signature.value);
        if (signature === null) throw new Error("Malformed signature");
        const publicKey = await importPublicKey(key.publicKeySpkiBase64url);
        attestationValid = await globalThis.crypto.subtle.verify(
          DM2016_DURABLE_ATTESTATION_ALGORITHM,
          publicKey,
          toArrayBuffer(signature),
          new TextEncoder().encode(canonicalJson(receipt.payload as unknown as JsonValue)),
        );
        if (!attestationValid) reasons.push("ATTESTATION_SIGNATURE_FAILED");
      } catch {
        reasons.push("ATTESTATION_PUBLIC_KEY_INVALID");
      }
    }
  }
  return freezeDeep({
    integrityValid,
    attestationValid,
    currentVerificationWindow,
    historicallyAuthentic: integrityValid && attestationValid,
    checkedAt: checkedAt.toISOString(),
    quoteSha256: document.payload.hashes.quoteSha256,
    resultSha256: document.payload.hashes.resultSha256,
    attestationKeyId: receipt.payload.attestationKeyId,
    reasons,
  });
}
